import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';
import ChevronUp from '@decisiv/iconix/lib/components/ChevronUp';

import Filter from '@decisiv/ui-components/lib/components/Filter';
import Popover from '@decisiv/ui-components/lib/components/Popover';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import SelectableOptionsList from '../../shared/SelectableOptionsList';
import {
  FilterPanel,
  FilterPanelDivider,
  FilterPanelStatus,
  FilterPanelTitle,
  FilterSearchRow,
} from './styles';
import useStagedSelection, {
  ALL_OPTION_ID,
} from '../../shared/useStagedSelection';

export { ALL_OPTION_ID };

/**
 * How close to the bottom counts as "fetch the next page".
 *
 * Roughly two 30px rows, so the next page is usually in place before the user
 * reaches the end rather than after they hit it. Same threshold AccountsField
 * uses.
 */
const LOAD_MORE_THRESHOLD_PX = 60;

/**
 * One filter in the users list's filter row (PD-4063).
 *
 * Every menu in the design shares this anatomy: a title, an optional
 * search-as-you-type input, a divider, the options, and a footer holding CLEAR
 * and APPLY. Only Accounts and Job Role get the search input; Permissions has a
 * shorter title block and none.
 *
 * Selection is multi-select and staged: checking boxes builds a draft, and
 * nothing reaches the caller until APPLY. That keeps one refetch per menu rather
 * than one per checkbox, and lets dismissing the menu leave the applied filter
 * untouched. The draft and the select-all row live in useStagedSelection, which
 * the wizard's Accounts field shares.
 *
 * Filter + Popover + OptionsList rather than a KeyDS composite: the Figma
 * "Action Filter" instance is Filter variant="action", and Menu crashes on the
 * React 16 portal pins (see ManagementLayout/UserMenu.jsx).
 *
 * A menu whose options are searched server side may also be paged (PD-4577):
 * pass `onLoadMore` with `hasMore` and `total`, and `options` is however many
 * pages have been loaded rather than all of them. This asks for the next page as
 * the list nears its bottom and reports the loaded count below it. Without
 * `onLoadMore` nothing changes — Permissions and Job Role hold their whole
 * lists.
 */
function FilterMenu({
  allOption,
  hasMore,
  isLoading,
  label,
  onApply,
  onLoadMore,
  onSearch,
  options,
  searchPlaceholder,
  searchable,
  selected,
  title,
  total,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  // The panel, held in state rather than a ref so the effect below runs as soon
  // as it exists: Popover mounts its content a tick after `visible` flips, so an
  // effect keyed on isOpen alone runs while the panel is still absent and, its
  // dependencies unchanged, never runs again.
  //
  // The panel, not the list. Resolving the <ul> here would cache it for the
  // panel's whole life, and the <ul> comes and goes underneath: a search
  // matching nothing renders the empty state in its place, so clearing that
  // search mounts a new <ul> while the listener sits on the detached old one.
  const [panelNode, setPanelNode] = useState(null);
  const panelRef = useCallback((node) => setPanelNode(node), []);

  // Accounts are searched server side, so the options arriving are already
  // narrowed. Job Role holds its whole list, so it narrows here.
  const visibleOptions = onSearch
    ? options
    : options.filter((option) =>
        option.label.toLowerCase().includes(query.trim().toLowerCase()),
      );

  const { draft, handleChange, items, selectedIds } = useStagedSelection({
    allOption,
    isOpen,
    options: visibleOptions,
    selected,
  });

  const Chevron = isOpen ? ChevronUp : ChevronDown;

  const close = () => {
    setIsOpen(false);
    setQuery('');
  };

  const apply = (values) => {
    onApply(values);
    close();
  };

  /**
   * Ask for the next page as the list nears its bottom.
   *
   * The listener goes on OptionsList's own scrolling <ul>: it already carries
   * `max-height` and `overflow: auto`, so it is the element that scrolls. Found
   * by query rather than by prop because OptionsList keeps its container ref in
   * internal state and forwards no way in.
   *
   * Guarded on scrollHeight because jsdom lays nothing out and reports every
   * dimension as 0, which would otherwise read as "already at the bottom" and
   * fetch as soon as the menu opened.
   */
  useEffect(() => {
    if (!panelNode || !hasMore || !onLoadMore) return undefined;

    const list = panelNode.querySelector('ul');
    if (!list) return undefined;

    const onScroll = () => {
      if (list.scrollHeight <= 0) return;

      const remaining = list.scrollHeight - list.scrollTop - list.clientHeight;

      if (remaining <= LOAD_MORE_THRESHOLD_PX) onLoadMore();
    };

    list.addEventListener('scroll', onScroll);
    return () => list.removeEventListener('scroll', onScroll);
  }, [hasMore, isLoading, onLoadMore, options.length, panelNode]);

  return (
    <Popover
      hideOnClickOutside
      onRequestHide={close}
      placement="bottom-start"
      showArrow={false}
      visible={isOpen}
      target={
        <Filter
          variant="action"
          active={selected.length > 0}
          count={selected.length || undefined}
          endIcon={Chevron}
          text={selected.length ? title : label}
          onClick={() => setIsOpen((open) => !open)}
        />
      }
    >
      <FilterPanel ref={panelRef}>
        <FilterPanelTitle>
          <P weight="semibold">{title}</P>
          {searchable && (
            <FilterSearchRow>
              {/* "Search as you type" in the design, which is
                  searchOn="change". Note SearchInput documents a
                  debounceTimeout it never reads, so onSearch fires per
                  keystroke — the caller debounces. */}
              <SearchInput
                hideLabel
                label={`Search ${title}`}
                placeholder={searchPlaceholder}
                searchOn="change"
                size="small"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onSearch={onSearch}
              />
            </FilterSearchRow>
          )}
        </FilterPanelTitle>
        <FilterPanelDivider />
        <SelectableOptionsList
          multiple
          items={items}
          // OptionsList defaults pointerEvents to "none", which leaves the rows
          // unclickable.
          pointerEvents="auto"
          selected={selectedIds}
          onChange={handleChange}
          // This logs "Invalid prop `actions`" whatever you pass it, including
          // the library's own documented shapes — its propType is declared
          // through react-desc's oneOfType, which mis-validates. The Footer
          // reads left/right at runtime and renders correctly.
          actions={{
            left: [
              {
                text: 'Clear',
                kind: 'secondary',
                size: 'small',
                onClick: () => apply([]),
              },
            ],
            right: [
              {
                text: 'Apply',
                size: 'small',
                onClick: () => apply(draft),
              },
            ],
          }}
        />
        {/* Below the list rather than as a row inside it: OptionsList's items
            are all selectable, so a status row would be tickable.

            This is what tells the user the list is partial — without it, a
            loaded page can look like the whole list. */}
        {onLoadMore && visibleOptions.length > 0 && (isLoading || hasMore) && (
          <FilterPanelStatus>
            <P color="alaskanHusky" size="small" style={{ margin: 0 }}>
              {isLoading
                ? `Loading ${title.toLowerCase()}...`
                : `Showing ${
                    visibleOptions.length
                  } of ${total} ${title.toLowerCase()}`}
            </P>
          </FilterPanelStatus>
        )}
      </FilterPanel>
    </Popover>
  );
}

FilterMenu.propTypes = {
  /** Adds a leading "All" row that selects or clears every option at once. */
  allOption: PropTypes.bool,
  /** Whether `total` exceeds the options loaded so far. Paged menus only. */
  hasMore: PropTypes.bool,
  /** Whether a page is in flight. Paged menus only. */
  isLoading: PropTypes.bool,
  /** Placeholder shown while nothing is selected, e.g. "All Accounts". */
  label: PropTypes.string.isRequired,
  onApply: PropTypes.func.isRequired,
  /** Supply to page server side; the menu calls it as the list nears its end. */
  onLoadMore: PropTypes.func,
  /** Supply to search server side; omit to narrow the given options locally. */
  onSearch: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  searchPlaceholder: PropTypes.string,
  searchable: PropTypes.bool,
  selected: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  /** The menu's heading, and the chip's text once something is selected. */
  title: PropTypes.string.isRequired,
  /** How many options match, loaded or not. Paged menus only. */
  total: PropTypes.number,
};

FilterMenu.defaultProps = {
  allOption: false,
  hasMore: false,
  isLoading: false,
  onLoadMore: undefined,
  onSearch: undefined,
  options: [],
  searchPlaceholder: undefined,
  searchable: false,
  selected: [],
  total: 0,
};

export default FilterMenu;
